import { useMemo } from "react";
import { useQuery, gql } from "@apollo/client";
import { useApolloClients } from "~/components/ApolloClients";

const GET_JOB_TITLES = gql`
  query GetInterviewGuideJobTitles {
    jobTitles {
      id
      name
    }
  }
`;

type JobTitle = { id: string; name?: string | null } | null;

/**
 * Fetches the organisation's configured job titles from organisation settings
 * (the `jobTitles` query on the employer gateway, backed by the organisations
 * service) and exposes the distinct, sorted names for the Interview Guides
 * job-title dropdowns.
 *
 * This is the canonical source of truth — the roles the org has set up in
 * settings — rather than titles derived from job listings. Like
 * {@link useDivisions}, the organisation is resolved from the auth context
 * server-side, so no `organisationId` argument is required.
 */
export function useJobTitles() {
  const { employerClient } = useApolloClients();

  const { data, loading } = useQuery(GET_JOB_TITLES, {
    client: employerClient,
    fetchPolicy: "cache-and-network",
  });

  const jobTitles = useMemo(() => {
    const names = ((data?.jobTitles ?? []) as JobTitle[])
      .map((j) => j?.name?.trim())
      .filter((n): n is string => Boolean(n));
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [data]);

  return { jobTitles, loading };
}
